import React from 'react';
import ModalComponent from '../../components/modal/modal';
import ModalBodyComponent from '../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../components/modal/modalFooterComponent';
import { Label } from '../../components/input-text/input.styled';
import { useWealthlaneAwardApi } from '../../hooks';
import { StreamedAwardAgreementImportService } from '@wealthlane/api-services/wealthlane-award-service';
import { StreamingFilePicker } from '../../components/streaming-upload';
import useStreamingUpload from '../../hooks/custom-hooks/useStreamingUpload';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';

interface AwardAgreementModalType {
  isModalOpen: boolean;
  closeModal: () => void;
}

const AwardAgreementModal = ({ isModalOpen, closeModal }: AwardAgreementModalType) => {
  const { handleRequest: awardRequest } = useWealthlaneAwardApi();

  const upload = useStreamingUpload({
    requiredKeys: ['templateXlsx'],
    onAfterComplete: async ({ sessionId }) => {
      // Call the Award Agreement Import API with the sessionId
      try {
        await awardRequest(
          StreamedAwardAgreementImportService.postApiAwardApiStreamedAwardAgreementImportPerformImport(
            sessionId
          )
        );
        toast.success(
          <ToastComponent label='Success' message='Award Agreement import job submitted.' />
        );
        closeModal();
      } catch (error) {
        // Error is already handled by awardRequest
        console.error('Award agreement import failed:', error);
      }
    },
    onError: (error, context) => {
      console.error(`Upload error in ${context.phase}:`, error);
    },
  });

  const handleClose = (): void => {
    upload.reset();
    closeModal();
  };

  const handleComplete = async (): Promise<void> => {
    await upload.complete();
  };

  const isLoading = upload.isUploading || upload.isCompleting;

  return (
    <ModalComponent
      show={isModalOpen}
      closeModal={handleClose}
      loading={false}
      size='md'
      title='Import Award Agreement'
      children={
        <div>
          <ModalBodyComponent>
            <div>
              <Label htmlFor='templateXlsx'>Upload Award Agreement Template</Label>
              <StreamingFilePicker
                id='templateXlsx'
                accept='.xlsx'
                multiple
                fileTypeHint='*XLSX Files'
                fileStates={upload.getFileGroup('templateXlsx')}
                onFilesChange={(files) => upload.addFilesToGroup('templateXlsx', files)}
                onRemoveFile={(key) => upload.removeFile(key)}
                onRetryFile={(key) => upload.retryFile(key)}
                disabled={upload.status === 'completed'}
                error={upload.validationErrors['templateXlsx']}
              />
            </div>
            <div className='mt-4'>
              <Label htmlFor='documentsZip'>Upload Award Agreement Documents Zip File</Label>
              <StreamingFilePicker
                id='documentsZip'
                accept='.zip'
                multiple
                fileTypeHint='*ZIP Files'
                fileStates={upload.getFileGroup('documentsZip')}
                onFilesChange={(files) => upload.addFilesToGroup('documentsZip', files)}
                onRemoveFile={(key) => upload.removeFile(key)}
                onRetryFile={(key) => upload.retryFile(key)}
                disabled={upload.status === 'completed'}
                error={upload.validationErrors['documentsZip']}
              />
            </div>
          </ModalBodyComponent>
          <ModalFooterComponent
            loading={isLoading}
            onCloseModal={handleClose}
            modalStatus='Add'
            onClickButton={handleComplete}
            valueChanged={!upload.canComplete}
          />
        </div>
      }
    />
  );
};

export default AwardAgreementModal;
